/**
 * The "you are in a call" strip for PHONES — a structural sibling of the
 * TimerBar and the ReminderBar, mounted in the same place for the same reason:
 * it must outlive every navigation. On desktop the call lives in the sidebar's
 * bottom-left panel instead (VoiceSidebarPanel, Discord-style); on a phone that
 * sidebar is a drawer, and a hang-up button hidden behind a drawer is no
 * hang-up button, so Layout shows this strip below `md` only.
 *
 * It renders nothing at all when you're not in a channel, so the strip costs a
 * `null` on every other page.
 */
import {
  Captions,
  Loader2,
  Mic,
  MicOff,
  MonitorOff,
  MonitorUp,
  PhoneOff,
  Volume2,
  X,
} from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { canShareScreen, useVoiceRoom } from '../../contexts/VoiceRoomContext';
import { SoundboardButton } from './SoundboardButton';
import { Avatar } from '../tasks/linear/Avatar';

const BUTTON_CLASS =
  'inline-flex items-center gap-1 rounded px-1.5 py-1 text-[12px] disabled:opacity-50';

/**
 * Amber while the SDK is re-establishing the connection. Green is a promise
 * that the other people can hear you, and during a reconnect that promise is
 * false — which is worse than showing nothing, because the user keeps talking.
 */
const TONE = {
  live: {
    bar: 'border-emerald-500/40 bg-emerald-500/10',
    icon: 'text-emerald-600',
    text: 'text-emerald-700',
    muted: 'text-emerald-600',
    button: 'text-emerald-700 hover:bg-emerald-500/15',
  },
  warn: {
    bar: 'border-amber-500/40 bg-amber-500/10',
    icon: 'text-amber-600',
    text: 'text-amber-700',
    muted: 'text-amber-600',
    button: 'text-amber-700 hover:bg-amber-500/15',
  },
} as const;

/** The bar proper. Split from the exported component so it only mounts in a call. */
function ConnectedDock() {
  const { t } = useI18n();
  const {
    channelName,
    status,
    transcribing,
    members,
    speakingIds,
    micEnabled,
    screenShareEnabled,
    leave,
    toggleMic,
    toggleScreenShare,
  } = useVoiceRoom();

  const name = channelName;
  const speaking = new Set(speakingIds);
  const settling = status !== 'connected';
  const tone = status === 'reconnecting' ? TONE.warn : TONE.live;
  const label =
    status === 'connecting'
      ? t('teamChat.voiceConnecting')
      : status === 'reconnecting'
        ? t('teamChat.voiceReconnecting')
        : t('teamChat.voiceConnected');

  return (
    <div className={`border-b ${tone.bar}`}>
      <div className="flex items-center gap-2 px-3 py-1.5">
        {settling ? (
          <Loader2 size={14} className={`shrink-0 animate-spin ${tone.icon}`} />
        ) : (
          <Volume2 size={14} className={`shrink-0 ${tone.icon}`} />
        )}
        <span className={`min-w-0 truncate text-[12px] font-medium ${tone.text}`}>
          {label}
          {name && <span className={tone.muted}> · {name}</span>}
        </span>
        {transcribing && (
          <span title={t('teamChat.voiceTranscriptHint')} className={`shrink-0 ${tone.icon}`}>
            <Captions size={13} />
          </span>
        )}

        {/* Everyone in the room, with a ring on whoever is actually talking —
            the one piece of feedback that tells you the call is alive. */}
        <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
          {members.map((m) => (
            <span
              key={m.userId}
              title={m.isLocal ? t('teamChat.voiceYou') : m.name}
              className={[
                'shrink-0 rounded-full ring-2 transition-colors',
                speaking.has(m.userId) ? 'ring-emerald-500' : 'ring-transparent',
                m.muted ? 'opacity-50' : '',
              ].join(' ')}
            >
              <Avatar name={m.name} size={20} />
            </span>
          ))}
        </span>

        <div className="flex shrink-0 items-center gap-0.5">
          {/* Absent, not disabled, where screen capture cannot work at all: on
              iOS `getDisplayMedia` does not exist in any browser, because capture
              is an OS function there. A greyed-out button would imply a fix. */}
          {canShareScreen() && (
            <button
              type="button"
              onClick={() => void toggleScreenShare()}
              title={screenShareEnabled ? t('teamChat.voiceStopSharing') : t('teamChat.voiceShare')}
              aria-pressed={screenShareEnabled}
              className={`${BUTTON_CLASS} ${tone.button}`}
            >
              {screenShareEnabled ? <MonitorOff size={13} /> : <MonitorUp size={13} />}
              <span className="hidden sm:inline">
                {screenShareEnabled ? t('teamChat.voiceStopSharing') : t('teamChat.voiceShare')}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={() => void toggleMic()}
            title={micEnabled ? t('teamChat.voiceMute') : t('teamChat.voiceUnmute')}
            aria-pressed={!micEnabled}
            className={`${BUTTON_CLASS} ${tone.button}`}
          >
            {micEnabled ? <Mic size={13} /> : <MicOff size={13} />}
            <span className="hidden sm:inline">
              {micEnabled ? t('teamChat.voiceMute') : t('teamChat.voiceUnmute')}
            </span>
          </button>
          <SoundboardButton className={`${BUTTON_CLASS} ${tone.button}`} />
          <button
            type="button"
            onClick={() => void leave()}
            title={t('teamChat.voiceLeave')}
            className={`${BUTTON_CLASS} text-red-600 hover:bg-red-500/15 hover:text-red-700`}
          >
            <PhoneOff size={13} />
            <span className="hidden sm:inline">{t('teamChat.voiceLeave')}</span>
          </button>
        </div>
      </div>

      {/* Phones only, and stated rather than fixed: iOS Safari suspends WebRTC
          when the tab goes to the background or the screen locks, and no
          amount of web code prevents it. Someone who switches apps mid-call and
          comes back to silence should have been told, not left guessing. */}
      <p className={`px-3 pb-1 text-[11px] leading-tight sm:hidden ${tone.muted}`}>
        {t('teamChat.voiceMobileHint')}
      </p>
    </div>
  );
}

/**
 * A join that failed leaves you nowhere — no dock, no channel — so without
 * this strip the click would simply do nothing and look like a dead button.
 */
function ErrorStrip() {
  const { t } = useI18n();
  const { error, dismissError } = useVoiceRoom();
  return (
    <div className="flex items-center gap-2 border-b border-red-500/40 bg-red-500/10 px-3 py-1.5">
      <Volume2 size={14} className="shrink-0 text-red-600" />
      <span className="min-w-0 flex-1 truncate text-[12px] text-red-600">{t(error!)}</span>
      <button
        type="button"
        onClick={dismissError}
        title={t('common.close')}
        className={`${BUTTON_CLASS} text-red-600 hover:bg-red-500/15 hover:text-red-700`}
      >
        <X size={13} />
      </button>
    </div>
  );
}

export function VoiceDock() {
  const { channelId, error } = useVoiceRoom();
  if (channelId) return <ConnectedDock />;
  if (error) return <ErrorStrip />;
  return null;
}
